import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Icon from '@mdi/react';
import { mdiFilter, mdiClose } from '@mdi/js';
import { List, TextField, InputAdornment, ListItem, Box, Paper, Typography, Divider, IconButton, Stack } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import TripListItem from './tripComponents/TripListItem';
import {
    tripListSelector, searchStageSelector, getActiveTrips, setSearchStage, setSelectedTrip
} from '../../features/tripcenter/tripControlSlice';
import { selectRouteList } from '../../features/tripcenter/routeSlice';

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const TripsSideBarBody = () => {
    const dispatch = useDispatch();
    const trips = useSelector(tripListSelector);
    const routeList = useSelector(selectRouteList);
    const searchStage = useSelector(searchStageSelector);
    const [expandedTripId, setExpandedTripId] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDate, setSelectedDate] = useState(null);
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    // Memoized filtered trips
    const filteredTrips = useMemo(() => {
        const query = searchQuery.toLowerCase();
        return trips.filter(trip => {
            // Text search
            const searchableFields = [
                trip.truckRegistrationNumber,
                trip.route?.routeName,
                trip.tripStage,
                trip.activeStatus,
                trip.currentLocation?.name,
                trip.driverName,
                trip.customer,
                trip.goodsName,
            ];
            const matchesText = searchQuery ? searchableFields.some(field =>
                field?.toString().toLowerCase().includes(query)
            ) : true;

            // Date filter
            let matchesDate = true;
            if (selectedDate) {
                console.log("Selected Date", selectedDate);
                console.log("Trip", trip.plannedStartTime, trip.actualStartTime);
                const selectedDateIST = dayjs(selectedDate).startOf('day');
                const plannedStartIST = dayjs(trip.plannedStartTime).tz('Asia/Kolkata').startOf('day');
                const actualStartIST = trip.actualStartTime ? dayjs(trip.actualStartTime).tz('Asia/Kolkata').startOf('day') : null;
                console.log("IST", selectedDateIST, plannedStartIST, actualStartIST)
                matchesDate = plannedStartIST.isSame(selectedDateIST) ||
                    (actualStartIST && actualStartIST.isSame(selectedDateIST));
            }

            return matchesText && matchesDate;
        });
    }, [trips, searchQuery, selectedDate]);

    useEffect(() => {
        if (!searchStage) {
            dispatch(setSearchStage('Active'));
            dispatch(getActiveTrips());
        }
    }, [dispatch, searchStage]);

    useEffect(() => {
        return () => {
            dispatch(setSelectedTrip(null));
        }
    }, [dispatch]);

    useEffect(() => {
        //console.log("trips List", trips);
    }, [trips]);

    return (
        <div style={{ marginTop: '5px', position: 'relative' }}>
            <List dense sx={{ bp: 0, borderRadius: 1, mt: 0 }}>
                <ListItem
                    sx={{
                        borderRadius: 0.5,
                        mb: 0.15,
                        boxShadow: 1,
                        bgcolor: '#fafafa',
                        padding: 1,
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        backgroundColor: '#fafafa'
                    }}
                >
                    <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                        <TextField
                            size="small"
                            placeholder="Filter trips..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{
                                flexGrow: 1,
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'white',
                                    '&:hover fieldset': {
                                        borderColor: 'primary.main',
                                    },
                                },
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon path={mdiFilter} size={0.8} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Count Display Box */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 80,
                                height: 40,
                                backgroundColor: 'white',
                                border: '1px solid #e0e0e0',
                                borderRadius: 1,
                                px: 1
                            }}
                        >
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                {filteredTrips.length} / {trips.length}
                            </Typography>
                        </Box>

                        {/* Date Filter Icon */}
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                open={datePickerOpen}
                                onOpen={() => setDatePickerOpen(true)}
                                onClose={() => setDatePickerOpen(false)}
                                value={selectedDate}
                                onChange={(newDate) => {
                                    setSelectedDate(newDate);
                                    setDatePickerOpen(false);
                                }}
                                slotProps={{
                                    textField: {
                                        sx: { display: 'none' }
                                    }
                                }}
                            />
                        </LocalizationProvider>

                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {selectedDate && (
                                <IconButton
                                    size="small"
                                    onClick={() => setSelectedDate(null)}
                                    sx={{
                                        backgroundColor: 'error.main',
                                        color: 'white',
                                        '&:hover': {
                                            backgroundColor: 'error.dark',
                                        },
                                        width: 40,
                                        height: 40
                                    }}
                                >
                                    <Icon path={mdiClose} size={0.8} />
                                </IconButton>
                            )}
                            {
                                !selectedDate &&

                                <IconButton
                                    size="small"
                                    onClick={() => setDatePickerOpen(true)}
                                    sx={{
                                        backgroundColor: selectedDate ? 'primary.main' : 'white',
                                        color: selectedDate ? 'white' : 'primary.main',
                                        '&:hover': {
                                            backgroundColor: selectedDate ? 'primary.dark' : 'grey.200',
                                        },
                                        width: 40,
                                        height: 40
                                    }}
                                >
                                    <CalendarTodayIcon />
                                </IconButton>
                            }
                        </Box>
                    </Stack>
                </ListItem>

                {filteredTrips && filteredTrips.length > 0 ? (
                    filteredTrips.map(trip => (
                        <TripListItem
                            key={trip._id}
                            tripData={trip}
                            expandedTripId={expandedTripId}
                            setExpandedTripId={setExpandedTripId}
                        />
                    ))
                ) : (
                        <Paper
                            elevation={0}
                            variant="outlined"
                            sx={{
                                m: 1,
                                p: 4,
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                                textAlign: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 2,
                                border: '1px dashed',
                                borderColor: 'divider'
                            }}
                        >
                            <SearchOffIcon sx={{ fontSize: 48, color: 'text.secondary' }} />

                            <Box>
                                <Typography variant="h6" color="text.primary" fontWeight={500} gutterBottom>
                                    No Trips Found
                                </Typography>

                                <Typography variant="body2" color="text.secondary">
                                    Try adjusting your search criteria or create a new trip
                                </Typography>
                            </Box>

                            <Divider sx={{ width: '40%', my: 1 }} />
                            {routeList?.length > 0 ? (
                                <Box>
                                    <Typography variant="body1" color="text.disabled">
                                        No trips found!
                                    </Typography>
                                    <Typography variant="body1" color="text.disabled">
                                        Modify your search criteria or create a new trip from Routes List Panel
                                    </Typography>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography variant="body1" color="text.disabled">
                                        You have no Routes!
                                    </Typography>
                                    <Typography variant="body1" color="text.disabled">
                                        Go to Route Builder to create a new Route
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                )}
            </List>
        </div>
    );
};

export default TripsSideBarBody;